import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../AuthContext.jsx";

const ZIP_RE = /^\d{5}(?:-\d{4})?$/;

export default function RegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [sensitivity, setSensitivity] = useState(5); // now 1..10
  const [homeZip, setHomeZip] = useState("");
  const [zipDirty, setZipDirty] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const normalizeZip = (z) => z.trim();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    if (password !== confirmPassword) {
      return setErr("Passwords do not match.");
    }

    const trimmedZip = normalizeZip(homeZip);
    if (trimmedZip && !ZIP_RE.test(trimmedZip)) {
      setZipDirty(true);
      return setErr("Please enter a valid ZIP code (12345 or 12345-6789).");
    }

    setLoading(true);
    try {
      await register({
        email,
        password,
        sensitivity,                 // sends 1..10 (backend accepts 0..10)
        home_zip: trimmedZip === "" ? null : trimmedZip,
      });
      nav("/account", { replace: true });
    } catch (e) {
      setErr(e.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const zipInvalid = zipDirty && homeZip && !ZIP_RE.test(homeZip);

  return (
    <div className="panel" style={{ maxWidth: 480, margin: "0 auto" }}>
      <h2>Create your account</h2>
      {err && <div className="error" aria-live="assertive">{err}</div>}

      <form onSubmit={onSubmit} className="form" noValidate>
        <label className="field" style={{marginTop: '6px'}}>
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>

        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>

        <label className="field">
          <span>Confirm password</span>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />
        </label>

        {/* Home ZIP code */}
        <label className="field">
          <span>Home ZIP code (optional)</span>
          <span>We use this to make sure we support your area.</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="postal-code"
            placeholder="e.g. 12345 or 12345-6789"
            value={homeZip}
            onChange={(e) => setHomeZip(e.target.value)}
            onBlur={() => setZipDirty(true)}
            aria-invalid={zipInvalid ? "true" : "false"}
            aria-describedby="zip-help"
            pattern="\d{5}(-\d{4})?"
          />
          {zipInvalid && <div className="field-error">Invalid ZIP format.</div>}
        </label>

        {/* Sensitivity as 10 buttons */}
        <div className="field sensitivity-field" style={{marginTop: '6px', marginBottom: '6px'}}>
          <span>Gluten sensitivity: {sensitivity}</span>
          <span>0 - Can eat gluten</span>
          <span>5 - Ok with a little cross-contamination</span>
          <span>10 - No cross-contamination allowed</span>
          <div
            style={{marginTop: '6px'}}
            className="segmented sensitivity-grid"
            role="group"
            aria-label="Gluten sensitivity from 1 to 10"
          >
            {Array.from({ length: 11 }, (_, i) => i).map((n) => (
              <button
                key={n}
                type="button"
                className={`seg ${sensitivity === n ? "selected" : ""}`}
                aria-pressed={sensitivity === n ? "true" : "false"}
                onClick={() => setSensitivity(n)}
                title={`Sensitivity ${n}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <p className="legal-text">
          By registering in, you agree to our{" "}
          <Link to="/privacy">Privacy Policy</Link> and{" "}
          <Link to="/terms">Terms & Conditions</Link>.
        </p>

        <button
          style={{marginTop: '6px'}}
          type="submit"
          className="btn btn-primary"
          disabled={loading}
          aria-busy={loading ? "true" : "false"}
        >
          {loading ? "Creating…" : "Create account"}
        </button>
      </form>

      <p style={{ marginTop: 12 }}>
        Already have an account? <Link to="/login">Log in</Link>
      </p>
    </div>
  );
}
